import { type ReactNode, Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { refreshPolicyVersions, useAgreementStatus } from './legal/agreement.js';
import { audioBus } from './table/AudioBus.js';
import { useAuthStore } from './store/authStore.js';
import { startCreditSync } from './session/credits.js';
import { syncOnTokenChange } from './session/offlineSync.js';
import { startPresence } from './store/presenceStore.js';
import { type SettingsState, useSettingsStore } from './store/settingsStore.js';
import { startPreferenceSync } from './store/preferenceSync.js';
import { warmOwnRig } from './ui/castFaces.js';
import { applyGround } from './ui/ground.js';
import { HomeScreen } from './ui/screens/HomeScreen.js';
import { StrayTableDoor } from './ui/screens/useSessionNav.js';
import { Screen } from './ui/Screen.js';
import { SettingsScreen } from './ui/screens/SettingsScreen.js';
import { RESET_LINK_PATH, accountPath } from './ui/screens/settings/accountPages.js';
import { WelcomeScreen } from './ui/screens/WelcomeScreen.js';
import { LoadingLine } from './ui/LoadingLine.js';

/**
 * Routes.
 *
 * Every screen but the menu and the settings is lazy: the menu should be
 * interactive before the table, its card art and the engine have been parsed,
 * which matters most on the phones we most want to run well.
 */
const lazyScreen = <K extends string>(
  load: () => Promise<Record<K, React.ComponentType>>,
  name: K,
): React.LazyExoticComponent<React.ComponentType> =>
  lazy(async () => ({ default: (await load())[name] }));

const LocalGameScreen = lazyScreen(
  () => import('./ui/screens/LocalGameScreen.js'),
  'LocalGameScreen',
);
const AuthScreen = lazyScreen(() => import('./ui/screens/AuthScreen.js'), 'AuthScreen');
const PlayScreen = lazyScreen(() => import('./ui/screens/PlayScreen.js'), 'PlayScreen');
const MatchmakingScreen = lazyScreen(
  () => import('./ui/screens/MatchmakingScreen.js'),
  'MatchmakingScreen',
);
const LobbyScreen = lazyScreen(() => import('./ui/screens/LobbyScreen.js'), 'LobbyScreen');
const OnlineGameScreen = lazyScreen(
  () => import('./ui/screens/OnlineGameScreen.js'),
  'OnlineGameScreen',
);
const HistoryScreen = lazyScreen(() => import('./ui/screens/HistoryScreen.js'), 'HistoryScreen');
const ReplayScreen = lazyScreen(() => import('./ui/screens/ReplayScreen.js'), 'ReplayScreen');
const ProfileScreen = lazyScreen(() => import('./ui/screens/ProfileScreen.js'), 'ProfileScreen');
const PrivacyScreen = lazyScreen(() => import('./ui/screens/LegalScreen.js'), 'PrivacyScreen');
const TermsScreen = lazyScreen(() => import('./ui/screens/LegalScreen.js'), 'TermsScreen');
const StatsScreen = lazyScreen(() => import('./ui/screens/StatsScreen.js'), 'StatsScreen');
const LearnScreen = lazyScreen(() => import('./ui/screens/LearnScreen.js'), 'LearnScreen');
const CrossLessonScreen = lazyScreen(
  () => import('./ui/screens/LessonScreens.js'),
  'CrossLessonScreen',
);
const CribbageLessonScreen = lazyScreen(
  () => import('./ui/screens/LessonScreens.js'),
  'CribbageLessonScreen',
);
const RulesScreen = lazyScreen(() => import('./ui/screens/RulesScreen.js'), 'RulesScreen');
const ScoringScreen = lazyScreen(() => import('./ui/screens/ScoringScreen.js'), 'ScoringScreen');
const TrainerScreen = lazyScreen(() => import('./ui/screens/TrainerScreen.js'), 'TrainerScreen');
/* The admin console's replay viewer. Not a player screen, but it ships: the
   production admin frames it on real games. */
const ReplayViewer = lazyScreen(() => import('./ui/screens/ReplayViewer.js'), 'ReplayViewer');

/*
 * The workshop. Lazy like everything else, and for a stronger reason: it
 * imports the whole table, the engine and every fixture it draws, and no player
 * ever asks for it. Nothing in `src/dev/` may be imported from outside it.
 *
 * Only a build that asks for it has it at all: the dev server, and the build
 * the e2e suite runs against (`VITE_WORKSHOP=1`). Everywhere else
 * `__WORKSHOP__` is a literal false, the import below is dead code, and a
 * production build carries no `/dev` route and no module from `src/dev/`.
 * `tools/workshopGuard.ts` fails that build if one gets in.
 */
const Workshop = __WORKSHOP__ ? lazyScreen(() => import('./dev/Workshop.js'), 'Workshop') : null;

export function App(): React.JSX.Element {
  useEffect(() => {
    void useAuthStore.getState().restore();
  }, []);

  /*
   * Games played offline are offered the moment there is a session to offer
   * them under: on launch, and again the instant the device registers or a
   * sign-in swaps the token. See `syncOnTokenChange`.
   */
  useEffect(() => syncOnTokenChange(), []);

  /*
   * The server's Terms and Privacy versions are the ones a player agrees to,
   * so a material change reaches every device without an app release. Pulled
   * at launch and again whenever the connection comes back.
   */
  useEffect(() => {
    // Not aborted on cleanup: a refresh only saves a document, which is as
    // true after an unmount as before it, and the app root unmounts only in
    // development, where StrictMode would cancel every first request.
    const refresh = (): void => {
      void refreshPolicyVersions();
    };
    refresh();
    globalThis.addEventListener('online', refresh);
    return () => {
      globalThis.removeEventListener('online', refresh);
    };
  }, []);

  /* The presence beat: keeps this player in the Play screen's online count
     while the app is open, including mid-game. See `presenceStore`. */
  useEffect(() => startPresence(), []);

  /*
   * A device that could not register tries again when the app comes back.
   *
   * The failure case is a first launch with no network: the app runs, the games
   * are played and stored, and there is nobody to stamp them with. Waiting for
   * a reload is the wrong answer on a phone, which is backgrounded far more
   * often than it is restarted. `restore` mints at most one device however many
   * times it is called, so this is safe to fire on every foreground.
   */
  useEffect(() => {
    const retry = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (useAuthStore.getState().token !== null) return;
      void useAuthStore.getState().restore();
    };
    document.addEventListener('visibilitychange', retry);
    return () => {
      document.removeEventListener('visibilitychange', retry);
    };
  }, []);

  // The Game settings follow the player between devices. See `preferenceSync.ts`.
  useEffect(() => startPreferenceSync(), []);

  // Hints and reviews spent on this device go up when it can. See `session/credits.ts`.
  useEffect(() => startCreditSync(), []);

  // A member's own face plays its rig at game over, offline too. See `warmOwnRig`.
  const ownFace = useAuthStore((state) => state.user?.avatar_id);
  useEffect(() => {
    warmOwnRig(ownFace);
  }, [ownFace]);

  /*
   * Audio lives at the app root, not the game screen: the browser only opens
   * the output from inside a real gesture, and the first gesture of a session
   * is almost always a menu tap long before a table exists.
   */
  useEffect(() => {
    const unlock = (): void => {
      audioBus.unlock();
    };
    globalThis.addEventListener('pointerdown', unlock, { once: true, passive: true });
    const apply = (state: SettingsState): void => {
      audioBus.setMuted(!state.sound);
      audioBus.setVolume(state.volume);
    };
    apply(useSettingsStore.getState());
    const unsubscribe = useSettingsStore.subscribe(apply);
    return () => {
      globalThis.removeEventListener('pointerdown', unlock);
      unsubscribe();
    };
  }, []);

  return (
    <Router>
      <Ground />
      <ScrollToTop />
      <StrayTableDoor />
      <Suspense fallback={<Loading />}>
        <AgreementGate>
          <Routes>
            <Route path="/" element={<HomeScreen />} />
            <Route path="/auth" element={<AuthScreen />} />
            <Route path="/play" element={<PlayScreen />} />
            <Route path="/learn" element={<LearnScreen />} />
            <Route path="/learn/cross" element={<CrossLessonScreen />} />
            <Route path="/learn/cribbage" element={<CribbageLessonScreen />} />
            <Route path="/learn/rules" element={<RulesScreen />} />
            <Route path="/learn/scoring" element={<ScoringScreen />} />
            <Route path="/trainer" element={<TrainerScreen />} />
            <Route path="/local" element={<LocalGameScreen />} />
            <Route path="/queue" element={<MatchmakingScreen />} />
            <Route path="/lobby/:code" element={<LobbyScreen />} />
            <Route path="/game/:gameId" element={<OnlineGameScreen />} />
            <Route path="/history" element={<HistoryScreen />} />
            <Route path="/history/:gameId" element={<ReplayScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/stats" element={<StatsScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="/settings/:group" element={<SettingsScreen />} />
            <Route path="/settings/:group/:page" element={<SettingsScreen />} />
            <Route path={RESET_LINK_PATH} element={<ResetLink />} />
            <Route path="/privacy" element={<PrivacyScreen />} />
            <Route path="/terms" element={<TermsScreen />} />
            <Route path="/embed/replay" element={<ReplayViewer />} />
            {Workshop !== null && <Route path="/dev/*" element={<Workshop />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AgreementGate>
      </Suspense>
    </Router>
  );
}

/**
 * What a device may open before it agrees to the terms (D10): the documents
 * themselves, which it must be able to read first, the lessons, which a web
 * visitor from search reads freely, the workshop, and the admin's framed
 * replay viewer, whose browser may never have agreed on the client's origin.
 * None of them creates a player, so none of them needs the agreement.
 */
const OPEN_BEFORE_AGREEMENT = ['/terms', '/privacy', '/learn', '/dev', '/embed'];

/**
 * Where a terms change waits its turn. A new version can arrive mid-session
 * from the server, and a game in progress is not interrupted for it: the
 * terms-changed screen stands in front of the next screen the player opens.
 */
const GAME_ROUTES = ['/game', '/local', '/queue', '/lobby'];

/**
 * Where a newcomer goes straight through, because the screen asks for the
 * agreement itself: an invitation's Join game reads Agree and join (the
 * `lobby-invites` sketch). The invitation creates nothing until that tap, and
 * the Welcome screen standing in front of it would hide the table the link
 * was sent for.
 */
const AGREES_ITSELF = ['/lobby'];

function under(pathname: string, roots: readonly string[]): boolean {
  return roots.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/**
 * The Welcome screen, in front of every other route until the device agrees.
 *
 * In front rather than redirected to: the address stays what the player
 * opened, so an invite or a game link goes on to where it was going after the
 * tap, and Back still leaves the app rather than bouncing off a redirect.
 */
function AgreementGate({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { pathname } = useLocation();
  const status = useAgreementStatus();
  if (status === 'current' || under(pathname, OPEN_BEFORE_AGREEMENT)) return <>{children}</>;
  if (status === 'none' && under(pathname, AGREES_ITSELF)) return <>{children}</>;
  if (status === 'outdated' && under(pathname, GAME_ROUTES)) return <>{children}</>;
  return <WelcomeScreen status={status} />;
}

/**
 * The password reset email's link, sent on to the page that sets the password.
 * The token rides along in the query. The link is not opened before the
 * agreement: a device that has not agreed meets the Welcome screen first, at
 * this same address, and comes here once it agrees.
 */
export function ResetLink(): React.JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`${accountPath('new-password')}${search}`} replace />;
}

function Loading(): React.JSX.Element {
  return (
    <Screen>
      <LoadingLine />
    </Screen>
  );
}

/*
 * Keeps the document's ground with the route. The first stamp is `ui/ground.ts`
 * at import time, which is what the workshop's green flash needed; this is the
 * same call on every navigation after it. It lives in the eager router, not in
 * the screen, because a lazy screen decides its ground too late to matter.
 */
function Ground(): null {
  const { pathname } = useLocation();
  const highContrast = useSettingsStore((state) => state.highContrastFelt);
  useLayoutEffect(() => {
    applyGround({ pathname });
  }, [pathname]);
  // The preference belongs to the whole app, including portals and the Cross
  // review/game-over branches that do not render a game-screen wrapper.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (highContrast) root.dataset.theme = 'high-contrast';
    else delete root.dataset.theme;
    return () => {
      delete root.dataset.theme;
    };
  }, [highContrast]);
  return null;
}

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    globalThis.scrollTo?.(0, 0);
  }, [pathname]);
  return null;
}
